<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { ConfigModule } from '~/types/config';
import { useConfigParserStore } from '~/stores/configParser';
import { useModuleSidebar } from '~/composables/useModuleSidebar';
import MobileSidebarToggle from '~/components/base/MobileSidebarToggle.vue';
import MobileSidebarBackdrop from '~/components/base/MobileSidebarBackdrop.vue';
import MobileSidebarCloseButton from '~/components/base/MobileSidebarCloseButton.vue';

const { t } = useI18n();

const props = withDefaults(defineProps<{
  sortedModules: ConfigModule[];
  loading: boolean;
  showTitle?: boolean;
}>(), {
  sortedModules: () => [],
  loading: false,
  showTitle: true
});

const configParserStore = useConfigParserStore();
const { isMobile, sidebarClass, toggleSidebar, closeSidebar } = useModuleSidebar();
</script>

<template>
  <div class="sidebar-wrapper">
    <!-- Mobile toggle button -->
    <MobileSidebarToggle
      :isMobile="isMobile"
      :toggleSidebar="toggleSidebar"
      :ariaLabel="t('common.actions.toggleMenu')"
    />

    <!-- Backdrop for mobile -->
    <MobileSidebarBackdrop
      :isMobile="isMobile"
      :isOpen="sidebarClass['sidebar-open']"
      :onClose="closeSidebar"
    />

    <!-- Sidebar -->
    <div
      class="module-sidebar"
      :class="sidebarClass"
    >
      <!-- HEADER -->
      <div class="space-y-1 pb-2 border-bottom-1" :style="{ borderColor: `hsl(var(--border))` }" v-if="props.showTitle !== false">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold" style="color:hsl(var(--foreground));">
            {{ $t('config.sidebar.modules') }}
          </h2>
          <!-- Close button for mobile -->
          <MobileSidebarCloseButton
            :isMobile="isMobile"
            :onClose="closeSidebar"
            :ariaLabel="t('common.actions.close')"
          />
        </div>
      </div>

      <!-- LIST AREA -->
      <div class="space-y-2">
        <!-- Loading spinner -->
        <div v-if="loading" class="flex justify-center py-10">
          <ProgressSpinner />
        </div>

        <!-- No modules message -->
        <Message
          v-else-if="!props.sortedModules || props.sortedModules.length === 0"
          severity="warn"
          :closable="false"
        >
          {{ $t('config.sidebar.noModules') }}
        </Message>

        <!-- Modules List -->
        <div v-else>
          <div
            v-for="module in props.sortedModules"
            :key="module.moduleId"
            @click="configParserStore.setSelectedModule(module.moduleId); isMobile && closeSidebar()"
            class="cursor-pointer rounded-xl p-2 transition group relative items-center"
            :class="configParserStore.getSelectedModule === module.moduleId ? 'selected-card' : 'default-card'"
          >
            <!-- Item Layout -->
            <div class="flex items-center gap-1 relative">
              <!-- LEFT: Icon Column -->
              <div class="flex-shrink-0 item-stretch">
                <i class="pi pi-cog text-lg card-icon"></i>
              </div>

              <!-- MIDDLE: Text Content -->
              <div class="flex-1 min-w-0">
                <div class="card-title sidebar-title-wrap">
                  {{ module.translation }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.module-sidebar {
  transition: transform 0.3s ease;
  width: fit-content;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.card-icon {
  color: hsl(var(--card-icon));
  font-size: 0.9rem;
  position: relative;
  top: 50%;
  transform: translate(-50%,-50%);
  margin-left: 7px;
}

.default-card {
  background: hsl(var(--card));
  margin-bottom: 5px;
  transition: var(--transition-smooth);
}

.default-card:hover {
  background: hsl(var(--accent));
  border-radius: 6px;
}

.selected-card {
  background: hsl(var(--accent));
  border-radius: 6px;
  box-shadow: 0 2px 6px hsl(var(--shadow-subtle));
  margin-bottom: 5px;
  transition: var(--transition-smooth);
}

.icon-btn-close {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: hsl(var(--foreground));
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-btn-close:hover {
  background: hsl(var(--card-foreground) / 0.06);
}

/* Mobile UI styles now in unified components */

@media (max-width: 768px) {
  .module-sidebar {
    /* Positioning handled by global layout.scss */
    background: transparent;
    border: none;
    padding: 0;
    width: 100%;
    height: 100%;
  }
}

/* Wrapper to manage mobile/desktop layout */
.sidebar-wrapper {
  display: contents;
}

@media (max-width: 768px) {
  .sidebar-wrapper {
    display: block;
  }
}

</style>